import {useTranslation} from 'react-i18next';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import {Box, Button, Stack} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {translations} from '../../../../../../i18n/translations';
import {EntitiesRepository} from '../../../../../../repositories/entities';
import {PaginatedTableData} from '../../../../../../interfaces/paginated-table-data';
import {Entity} from '../../../../../../backend/interfaces/entity';
import {useState} from 'react';
import {ScreenPaths} from '../../../../../../enums/screen-path';
import {AlertModal} from '../../../../../../components/alert-modal/alert-modal';
import {PaginatedTable} from '../../../../../../components/paginated-table/paginated-table';
import {usePaginatedTable} from '../../../../../../hooks';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import SearchInput from '../../../../../../components/search-input/search-input';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Type = 'type',
  State = 'state',
}

export default function EntitiesSystemAdministratorOfficeScreen() {
  const {t} = useTranslation();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {path} = useRouteMatch();
  let history = useHistory();
  const [deletedEntityName, setDeletedEntityName] = useState<string | null>(
    new URLSearchParams(useLocation().search).get(ScreenPaths.EntitiesDeletedQuery)
  );

  // Fetch entities
  const handleGetEntities = async (limit: number, skip: number, search?: string) => {
    // Load entities
    const result: {totalCount: number; data: Array<Entity>} = await EntitiesRepository.instance.get(
      skip,
      limit,
      search
    );

    // Process data
    const processedData: PaginatedTableData<Entity> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        enabled: obj.isActive,
        avatarUrl: obj.picture,
        toLink: `${path}/${obj.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            value: obj.name || '',
          },
          {
            columnId: ColumnIdentifier.Type,
            value: t(translations.entity_types[obj.type]) || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.isActive
              ? t(translations.entities.root.state_value_true)
              : t(translations.entities.root.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
  } = usePaginatedTable<Entity>({
    fetchData: handleGetEntities,
  });

  // Handle close dialog
  const handleCloseDialog = () => {
    // Reset state
    setDeletedEntityName(null);

    // Reset history
    history.replace(path);
  };

  return (
    <>
      <GenericHeader
        pageTitle={t(translations.entities.root.title)}
        isLoading={false}
        leftColumn={
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              paddingTop: '10px',
            }}
          >
            <SearchInput
              placeholder={t(translations.entities.root.search_input_placeholder)}
              value={currentSearch}
              onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
              onSearchAction={(value: string) => handleApplyFilters(1, value)}
            />
          </Stack>
        }
        rightColumn={
          <Button
            endIcon={<AddIcon />}
            onClick={() => history.push(`${path}/create`)}
            variant="inverted"
          >
            {t(translations.entities.root.add_button)}
          </Button>
        }
      />
      <Box className="listing-container">
        <Box className="listing-container-centered">
          <PaginatedTable
            avatar={{type: 'entity'}}
            emptyTableMessage={t(translations.entities.root.no_entities)}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(translations.entities.root.name_header),
                flexGrow: 2,
              },
              {
                columnId: ColumnIdentifier.Type,
                title: t(translations.entities.root.type_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.entities.root.state_header),
                flexGrow: 1,
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={true}
          />
        </Box>
      </Box>
      <AlertModal
        id="entity-deleted-warning"
        opened={deletedEntityName ? true : false}
        title={t(translations.entities.root.deleted_entity_dialog_title, {
          name: deletedEntityName,
        })}
        content={t(translations.entities.root.deleted_entity_dialog_message)}
        handleClose={handleCloseDialog}
        actions={[
          {
            label: t(translations.entities.root.deleted_entity_dialog_ok_button),
            type: 'neutral',
            handleClick: handleCloseDialog,
          },
        ]}
      />
    </>
  );
}
